import {
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  FolderOpen,
  FileText,
  Workflow as WorkflowIcon,
  User as UserIcon,
  Bell,
  Loader2,
  X,
  Command,
  CornerDownLeft,
  LayoutGrid,
} from "lucide-react";
import globalSearchService, {
  GlobalSearchHit,
  GlobalSearchResult,
  SearchHitKind,
} from "../services/globalSearch.service";

/**
 * Navbar global search.
 *
 * Layout: an input pill that expands into a results panel when focused.
 * The panel groups results by kind (Folders / Files / Workflows / Users
 * / Notifications) and supports keyboard navigation: ⌘K / Ctrl+K to
 * focus, ↑↓ to move highlight, Enter to open, Esc to close.
 *
 * Backed by `globalSearchService.search()` which fans out to every
 * existing list endpoint in parallel — no new backend.
 */

const KIND_META: Record<SearchHitKind, { label: string; icon: JSX.Element; tint: string }> = {
  page: {
    label: "Pages",
    icon: <LayoutGrid className="w-3.5 h-3.5 text-indigo-500" />,
    tint: "text-indigo-600 bg-indigo-50",
  },
  folder: {
    label: "Folders",
    icon: <FolderOpen className="w-3.5 h-3.5 text-blue-500" />,
    tint: "text-blue-600 bg-blue-50",
  },
  file: {
    label: "Files",
    icon: <FileText className="w-3.5 h-3.5 text-emerald-500" />,
    tint: "text-emerald-600 bg-emerald-50",
  },
  workflow: {
    label: "Workflows",
    icon: <WorkflowIcon className="w-3.5 h-3.5 text-violet-500" />,
    tint: "text-violet-600 bg-violet-50",
  },
  user: {
    label: "Users",
    icon: <UserIcon className="w-3.5 h-3.5 text-rose-500" />,
    tint: "text-rose-600 bg-rose-50",
  },
  notification: {
    label: "Notifications",
    icon: <Bell className="w-3.5 h-3.5 text-sky-500" />,
    tint: "text-sky-600 bg-sky-50",
  },
};

function GlobalSearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Hotkey: ⌘K / Ctrl+K to focus ────────────────────────────────────
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ─── Click outside to close ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ─── Debounce input → fetch ──────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(query), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!debounced.trim()) {
        setResults(null);
        return;
      }
      setLoading(true);
      try {
        const res = await globalSearchService.search(debounced);
        if (!cancelled) {
          setResults(res);
          setHighlight(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  // Flat list of all hits in display order — used for keyboard nav.
  // Pages first so menu navigation is always the fastest match.
  const flatHits: GlobalSearchHit[] = useMemo(() => {
    if (!results) return [];
    return [
      ...results.pages,
      ...results.folders,
      ...results.files,
      ...results.workflows,
      ...results.users,
      ...results.notifications,
    ];
  }, [results]);

  const goToHit = useCallback(
    (hit: GlobalSearchHit) => {
      setOpen(false);
      navigate(hit.href);
    },
    [navigate],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flatHits.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % flatHits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + flatHits.length) % flatHits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = flatHits[highlight];
      if (hit) goToHit(hit);
    }
  };

  const showEmpty = open && !!query.trim() && !loading && results && results.total === 0;
  const showResults = open && !!query.trim() && !!results && results.total > 0;
  const showHero = open && !query.trim();

  return (
    <div ref={wrapperRef} className="relative flex-1 max-w-[520px]">
      {/* Trigger / input pill */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          id="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search pages, folders, files, workflows, users…"
          className="w-full pl-9 pr-20 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
        />
        {/* Right adornments — clear button or hotkey hint */}
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
            title="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-slate-500 bg-white border border-slate-200 rounded">
            <Command className="w-2.5 h-2.5" />
            K
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 max-h-[70vh] flex flex-col">
          {loading && (
            <div className="px-4 py-6 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching…
            </div>
          )}

          {showHero && (
            <div className="px-5 py-8 text-center text-slate-500">
              <SearchIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">
                Search across DocxIQ
              </p>
              <p className="text-[11px] mt-1.5 text-slate-400">
                Pages · Folders · Files · Workflows · Users · Notifications
              </p>
              <p className="text-[10px] mt-3 text-slate-400">
                Press{" "}
                <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono">
                  <Command className="w-2.5 h-2.5" />K
                </kbd>{" "}
                to open from anywhere
              </p>
            </div>
          )}

          {showEmpty && (
            <div className="px-5 py-10 text-center text-slate-500">
              <SearchIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No matches for "{query}"</p>
              <p className="text-[11px] mt-1 text-slate-400">
                Try a different keyword or a folder name.
              </p>
            </div>
          )}

          {showResults && results && (
            <div className="overflow-y-auto chat-scrollbar">
              {(["page", "folder", "file", "workflow", "user", "notification"] as const).map(
                (kind) => {
                  const hits =
                    kind === "page"
                      ? results.pages
                      : kind === "folder"
                        ? results.folders
                        : kind === "file"
                          ? results.files
                          : kind === "workflow"
                            ? results.workflows
                            : kind === "user"
                              ? results.users
                              : results.notifications;
                  if (hits.length === 0) return null;
                  const meta = KIND_META[kind];
                  // Compute the global highlight index for each row so
                  // keyboard nav lights up the correct one across groups.
                  const startIdx = flatHits.findIndex((h) => h === hits[0]);
                  return (
                    <Fragment key={kind}>
                      <div className="sticky top-0 px-4 py-1.5 bg-slate-50/95 backdrop-blur text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-100 flex items-center gap-1.5">
                        {meta.icon}
                        {meta.label}
                        <span className="text-slate-400">· {hits.length}</span>
                      </div>
                      <ul>
                        {hits.map((hit, i) => {
                          const idx = startIdx + i;
                          const isHighlighted = idx === highlight;
                          return (
                            <li key={hit.id}>
                              <button
                                type="button"
                                onMouseEnter={() => setHighlight(idx)}
                                onClick={() => goToHit(hit)}
                                className={`w-full text-left flex items-start gap-3 px-4 py-2.5 transition-colors ${
                                  isHighlighted ? "bg-blue-50" : "hover:bg-slate-50"
                                }`}
                              >
                                <span
                                  className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${meta.tint}`}
                                >
                                  {meta.icon}
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-sm text-slate-900 truncate">
                                    {hit.title}
                                  </span>
                                  {hit.subtitle && (
                                    <span className="block text-[11px] text-slate-500 truncate">
                                      {hit.subtitle}
                                    </span>
                                  )}
                                </span>
                                {isHighlighted && (
                                  <CornerDownLeft className="w-3.5 h-3.5 text-slate-400 self-center" />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </Fragment>
                  );
                },
              )}
            </div>
          )}

          {/* Footer — keyboard hints */}
          {open && !showHero && (
            <div className="border-t border-slate-100 px-3 py-1.5 bg-slate-50/80 flex items-center gap-3 text-[10px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[9px] font-mono">
                  ↑↓
                </kbd>
                navigate
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[9px] font-mono">
                  ↵
                </kbd>
                open
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[9px] font-mono">
                  esc
                </kbd>
                close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearchBar;
